// @ts-nocheck
import RenderField from './RenderField';
import DeleteDayService from './DeleteDayService';
import CircularProgress from '@mui/material/CircularProgress';
import ServiceFormSkeleton from '../../Services/ServiceFormSkeleton/ServiceFormSkeleton';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import { useScrollIntoView } from '../../../../hooks/useScrollIntoView';
import { Box, Grid } from '@mui/material';
import { PlusCircle, Trash, Books } from '@phosphor-icons/react';
import { XGDropdownMenu, XGButton } from 'xg-cl';
import {
  formatServiceTypeOptions,
  generateGridColumns,
  generateGridRows,
  validateFormData,
  generateServiceTypeFieldId,
  getFormPayload,
  getSingleFieldValueByKey,
  setSingleFieldValueByKey,
  focusFieldInput
} from '../../../../utils/serviceTypes/serviceTypes';
import {
  createServiceType,
  updateServiceType
} from '../../../../services/servicetype/servicetype';
import {
  createLibraryService,
  updateLibraryService
} from '../../../../services/library/service';
import { ErrorToast, SuccessToast } from '../../../../utils/alerts';
import {
  fileType,
  pathReference as PathReference
} from '../../../../constants/files';
import {
  getPresignedUrl,
  uploadToBucket
} from '../../../../services/upload/upload';
import { blue } from '@mui/material/colors';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import { serviceTypes as serviceTypesConstants } from '../../../../constants/serviceTypes';
import { getCheckInDate, getCheckOutDate } from '../../../../utils/dateUtility';
import * as PropTypes from 'prop-types';
import * as P from '@phosphor-icons/react';
import './ServiceTypesForm.css';
import { copyTripServicePhotoToLibrary } from '../../../../services/photo/photo';

const ServiceTypesForm = ({
  id,
  tripid,
  tripdetaileddayid,
  serviceType,
  onChange,
  onError,
  formData,
  serviceTypes,
  isLoading,
  onCancel,
  onSave,
  onServiceTypeChange,
  updateFormData,
  canDelete,
  onDelete,
  onSaveToLibrary,
  isEditing,
  isLibrary = false,
  isLoadingLibraryForm,
  showCustomOptions = false,
  dayDate,
  toggleKey,
  ...rest
}) => {
  const { servicetypefield = [] } = serviceType || {};
  const { t } = useTranslation(['common', 'trip', 'errors']);
  const { itineraryNavigation, enableStockPhotoPicker = false } = useFlags();
  const trip = useSelector((state) => state.trip);
  const iconref = serviceType?.iconref || serviceType?.servicetype?.iconref;
  const serviceTypeName = serviceType?.name || serviceType?.servicetype?.name;
  const serviceTypeCode = serviceType?.code || serviceType?.servicetype?.code;
  const isAccommodation =
    serviceTypeCode === serviceTypesConstants.ACCOMMODATION;
  const shouldDisableDates =
    isAccommodation && itineraryNavigation && !isLibrary;
  const formRefs = useRef({});
  const { scrollTo, isInViewport } = useScrollIntoView();
  const [isDeletingDayService, setIsDeletingDayService] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingToLibrary, setIsSavingToLibrary] = useState(false);
  const importingPhotoFields = useRef(new Set());
  const [isImportingPhotos, setIsImportingPhotos] = useState(false);
  const handlePhotoImportStateChange = useCallback((fieldId, importing) => {
    if (importing) importingPhotoFields.current.add(fieldId);
    else importingPhotoFields.current.delete(fieldId);
    setIsImportingPhotos(importingPhotoFields.current.size > 0);
  }, []);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [hasInitializedAccordions, setHasInitializedAccordions] =
    useState(false);
  const serviceModuleName = 'serviceTypesForm';
  const { pendingAction, setPending, hasUnsavedChanges } =
    useUnsavedChanges(serviceModuleName);
  const formContainerRef = useRef(null);

  const iconExists = (iconName) => {
    return typeof P[iconName] === 'object';
  };

  const gridRows = generateGridRows(
    servicetypefield,
    serviceType?.servicetype || serviceType,
    itineraryNavigation,
    isLibrary
  );
  const ServiceTypeIcon = iconExists(iconref) ? P[iconref] : null;
  const hasRows = gridRows && Object.keys(gridRows).length > 0;

  useEffect(() => {
    const executeAction = async () => {
      if (
        itineraryNavigation &&
        pendingAction === 'save' &&
        !isImportingPhotos
      ) {
        setPending('submitting');
        await handleSave();
      }
    };
    executeAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    itineraryNavigation,
    pendingAction,
    isLibrary,
    setPending,
    isImportingPhotos
  ]);

  useEffect(() => {
    if (serviceType?.servicetypeid) {
      setShouldAutoScroll(true);
    }
  }, [serviceType?.servicetypeid]);

  useEffect(() => {
    if (
      !isLibrary &&
      shouldAutoScroll &&
      serviceType &&
      formData &&
      Object.keys(formData).length > 0 &&
      hasRows
    ) {
      const scrollToTop = () => {
        const containers = [formContainerRef.current].filter(Boolean);

        let scrolled = false;

        for (const container of containers) {
          try {
            if (container && container.scrollTop !== undefined) {
              container.scrollTop = 0;
              scrolled = true;
              break;
            }
          } catch (error) {
            console.warn('Failed to scroll container:', error);
          }
        }

        return scrolled;
      };

      // Try immediate scroll
      const immediateSuccess = scrollToTop();

      if (!immediateSuccess) {
        // If immediate scroll fails, try with delays

        const timeouts = [200, 300, 600];
        timeouts.forEach((delay) => {
          setTimeout(scrollToTop, delay);
        });
      }

      setShouldAutoScroll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoScroll, serviceType, Object.keys(formData).length, hasRows]);

  useEffect(() => {
    if (
      hasRows &&
      serviceType &&
      Object.keys(formData).length > 0 &&
      !isLibrary
    ) {
      // Small delay to ensure DOM is fully rendered
      const container = document.querySelector(
        '.day-service-edit-slideout__content'
      );
      if (container && !isLibrary) {
        container.style.overflow = 'hidden';
      }

      const timer = setTimeout(() => {
        focusFieldInput(formRefs);

        if (container && !isLibrary) {
          container.scrollTop = 0;
          container.style.overflow = 'auto';
        }
      }, 300);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRows, serviceType?.servicetypeid]);

  const handleOnChange = useCallback(
    (serviceFieldType, value, additionalData) => {
      if (onChange) {
        onChange(serviceFieldType, value, additionalData);
      }
    },
    [onChange]
  );

  const handleOnError = useCallback(
    (servicefieldid, errorMessage = '') => {
      if (onError) {
        onError(servicefieldid, errorMessage);
      }
    },
    [onError]
  );

  const setFormFieldRef = (fieldName) => (element) => {
    formRefs.current[fieldName] = element;
  };

  const getAccommodationDates = (tripDayDate, numberOfNights = '1') => {
    if (!tripDayDate || !numberOfNights) {
      return { checkInDate: null, checkOutDate: null };
    }

    // Parse and validate numberOfNights
    const nights = parseInt(numberOfNights, 10);
    const validNights = !isNaN(nights) && nights > 0 ? nights : 1;
    // Calculate check-in and check-out dates
    const checkInDate = getCheckInDate(dayDate, 0);
    const checkOutDate = getCheckOutDate(checkInDate, validNights);

    return { checkInDate, checkOutDate };
  };

  // Initialize accordion state for Internal Notes
  useEffect(() => {
    if (serviceType && !hasInitializedAccordions) {
      const isNewService = !serviceType?.tripdetaileddayserviceid && !isEditing;
      const storedAccordionState = localStorage.getItem(
        `accordion-state-${toggleKey}`
      );

      let initialState = {};

      if (storedAccordionState) {
        // Use stored state from localStorage
        try {
          initialState = JSON.parse(storedAccordionState);
        } catch (error) {
          console.warn('Failed to parse stored accordion state:', error);
        }
      } else {
        // Default state for new trips: Internal Notes expanded
        initialState = {
          internalNotes: isNewService || !isLibrary // Expanded by default for new services
        };
      }

      setExpandedSections(initialState);
      setHasInitializedAccordions(true);
    }
  }, [serviceType, hasInitializedAccordions, isEditing, isLibrary, toggleKey]);

  const handleAccordionChange = (section) => {
    return (event, isExpanded) => {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: isExpanded
      }));
    };
  };

  useEffect(() => {
    if (hasInitializedAccordions && toggleKey) {
      localStorage.setItem(
        `accordion-state-${toggleKey}`,
        JSON.stringify(expandedSections)
      );
    }
  }, [expandedSections, hasInitializedAccordions, toggleKey]);

  const renderFields = useCallback(() => {
    return (
      <Grid container spacing={2} sx={{ gap: '4px' }}>
        {Object.keys(gridRows).map((rowNumber) => {
          const gridRowColumns = generateGridColumns(gridRows[rowNumber]);
          const columnKeys = gridRowColumns?.map((column) => column?.key);
          const alignBaseLine = columnKeys?.some(
            (key) => key === 'UPLOADIMAGES' || key === 'UPLOADATTACHMENTS'
          );

          // Set check in and check out dates for accommodation services
          if (shouldDisableDates) {
            const numberOfNights = getSingleFieldValueByKey(
              formData,
              'NUMBEROFNIGHTS'
            );
            const { checkInDate, checkOutDate } = getAccommodationDates(
              dayDate,
              numberOfNights || 1
            );

            setSingleFieldValueByKey(formData, 'CHECKINDATE', checkInDate);
            setSingleFieldValueByKey(formData, 'CHECKOUTDATE', checkOutDate);
          }

          return (
            <Grid item xs={12} key={rowNumber}>
              <Grid
                container
                spacing={2}
                sx={{ alignItems: alignBaseLine ? 'baseline' : 'end' }}
              >
                {gridRowColumns?.map((field = {}, index) => {
                  return (
                    <Grid
                      item
                      xs={field?.colsize}
                      key={field?.servicetypefieldid ?? index}
                    >
                      {!field?.isEmptyColumn ? (
                        <RenderField
                          key={field?.servicetypefieldid}
                          field={field}
                          onChange={handleOnChange}
                          onError={handleOnError}
                          formData={formData}
                          setFormFieldRef={setFormFieldRef}
                          disabled={
                            isSaving ||
                            isLoading ||
                            isImportingPhotos ||
                            !field.isenabled
                          }
                          isLibrary={isLibrary}
                          showCustomOptions={showCustomOptions}
                          dayDate={dayDate}
                          expandedSections={expandedSections}
                          onAccordionChange={handleAccordionChange}
                          serviceId={
                            serviceType?.tripdetaileddayserviceid ||
                            serviceType?.itineraryserviceid
                          }
                          tripId={tripid}
                          destinationTerm={
                            trip?.city?.cityname ||
                            trip?.country?.countryname ||
                            ''
                          }
                          tripTitle={trip?.tripname || ''}
                          onPhotoImportStateChange={
                            handlePhotoImportStateChange
                          }
                        />
                      ) : (
                        <></>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleOnChange,
    handleOnError,
    isSaving,
    formData,
    isLibrary,
    isLoading,
    isImportingPhotos,
    handlePhotoImportStateChange,
    gridRows,
    showCustomOptions
  ]);

  if (isLoading || isLoadingLibraryForm) {
    return <ServiceFormSkeleton id='service-types-button-skeleton' />;
  }

  const getFileName = (url = '') => {
    try {
      const urlObj = new URL(url, window.location.origin);
      const pathname = urlObj?.pathname || '';
      return pathname.substring(pathname.lastIndexOf('/') + 1);
    } catch (e) {
      const clean = String(url || '').split('?')[0];
      return clean.substring(clean.lastIndexOf('/') + 1);
    }
  };

  const handleSaveToLibrary = async () => {
    await handleSave({ isSaveToLibrary: true });
  };

  const handleSave = async (options = {}) => {
    if (importingPhotoFields.current.size > 0) return;
    const { isSaveToLibrary = false } = options;
    const errorText = isSaveToLibrary
      ? t('errors:libraryServiceCouldNotBeCreated')
      : isEditing
        ? t('errors:serviceTypeCouldNotBeUpdated')
        : t('errors:serviceTypeCouldNotBeCreated');
    try {
      if (isSaveToLibrary) {
        setIsSavingToLibrary(true);
      } else {
        setIsSaving(true);
      }

      let payload = getFormPayload(serviceType, formData, isLibrary);

      const { isValid, newFormData } = validateFormData(
        servicetypefield,
        formData,
        t
      );

      if (!isValid) {
        const formFields = servicetypefield?.sort((a, b) => a?.row - b?.row);
        let scrollToElement = null;

        for (let field of formFields) {
          const hasError = newFormData[field?.servicefieldid]?.error;

          if (hasError) {
            const fieldId = generateServiceTypeFieldId(field);
            scrollToElement = formRefs?.current[fieldId];
            break;
          }
        }

        if (scrollToElement) {
          const isVisible = isInViewport(scrollToElement);

          //Scroll to first field with error when not in viewport
          if (!isVisible) scrollTo(scrollToElement, 'top', 'smooth');

          setTimeout(() => {
            updateFormData(newFormData);
          }, 200);
        } else {
          updateFormData(newFormData);
        }
      } else {
        const fileFields = isSaveToLibrary
          ? payload?.servicefields?.filter((field) => field?.files?.length > 0)
          : payload?.servicefields?.filter((field) => field?.isFile);

        const imagesToUpload = [];
        const imagesToCopy = [];
        const attachmentsToUpload = [];

        fileFields?.forEach((field) => {
          field?.options?.forEach((option, optionIndex) => {
            const file = option?.file;
            if (
              enableStockPhotoPicker &&
              isSaveToLibrary &&
              option?.presignType === fileType.IMAGE &&
              option?.value &&
              (option?.stockImported || !option?.isNew)
            ) {
              imagesToCopy.push({
                fileName: getFileName(option.value).replace(
                  /^(BL|TH|SM|MD|LG)-/,
                  ''
                ),
                fieldIndex: field.fieldIndex,
                optionIndex
              });
              return;
            }
            if (
              file &&
              ((option?.isNew && !option?.stockImported) || isSaveToLibrary)
            ) {
              const pathReference =
                isLibrary || isSaveToLibrary
                  ? PathReference.LIBRARY
                  : PathReference.DETAILED_DAY_SERVICE;
              if (option?.presignType === 'image') {
                imagesToUpload.push({
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  pathReference,
                  tripId: tripid,
                  index: field?.fieldIndex,
                  optionIndex,
                  file,
                  servicefieldid: field?.servicefieldid
                });
              } else {
                attachmentsToUpload.push({
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  pathReference,
                  tripId: tripid,
                  index: field?.fieldIndex,
                  optionIndex,
                  file,
                  servicefieldid: field?.servicefieldid
                });
              }
            }
          });
        });

        if (imagesToCopy.length > 0) {
          try {
            await Promise.all(
              imagesToCopy.map(
                async ({ fileName, fieldIndex, optionIndex }) => {
                  const copied = await copyTripServicePhotoToLibrary({
                    tripId: tripid,
                    fileName
                  });
                  if (!copied?.fileName)
                    throw new Error('Missing copied image');
                  payload.servicefields[fieldIndex].options[optionIndex].value =
                    copied.fileName;
                }
              )
            );
          } catch (error) {
            ErrorToast(errorText);
            return;
          }
        }

        // Upload Images
        if (imagesToUpload.length > 0) {
          const imagesPayload = {
            files: imagesToUpload,
            fileType: fileType.IMAGE
          };

          const { data, error } = await getPresignedUrl(imagesPayload);

          if (error || !data) {
            ErrorToast(errorText);
            return;
          }

          const imagesPromises = [];

          if (data?.length > 0) {
            data.forEach((image, index) => {
              imagesPromises.push(
                uploadToBucket(image, imagesToUpload[index].file).then(() => {
                  const newFileName = getFileName(image?.data?.presignedUrl);
                  const fieldIndex = imagesToUpload[index]?.index;
                  const optionIndex = imagesToUpload[index]?.optionIndex;
                  payload.servicefields[fieldIndex].options[optionIndex].value =
                    newFileName;
                })
              );
            });

            try {
              await Promise.all(imagesPromises);
            } catch (error) {
              // Distinguish timeout from other errors
              if (error.code === 'ECONNABORTED') {
                ErrorToast(t('errors:imageUploadTimedOut'));
              } else if (error.code === 'ERR_NETWORK') {
                ErrorToast(t('errors:networkConnectionLost'));
              } else {
                ErrorToast(t('errors:imageUploadFailed'));
              }
              return;
            }
          }
        }

        // upload Attachments
        if (attachmentsToUpload.length > 0) {
          const attachmentsPayload = {
            files: attachmentsToUpload,
            fileType: fileType.FILE
          };

          const { data, error } = await getPresignedUrl(attachmentsPayload);

          if (error || !data) {
            ErrorToast(errorText);
            return;
          }

          const attachmentsPromises = [];

          if (data?.length > 0) {
            data.forEach((image, index) => {
              attachmentsPromises.push(
                uploadToBucket(image, attachmentsToUpload[index].file).then(
                  () => {
                    const newFileName = getFileName(image?.data?.presignedUrl);
                    const fieldIndex = attachmentsToUpload[index]?.index;
                    const optionIndex = attachmentsToUpload[index]?.optionIndex;
                    payload.servicefields[fieldIndex].options[
                      optionIndex
                    ].value = newFileName;
                  }
                )
              );
            });

            try {
              await Promise.all(attachmentsPromises);
            } catch (error) {
              // Distinguish timeout from other errors
              if (error.code === 'ECONNABORTED') {
                ErrorToast(t('errors:attachmentUploadTimedOut'));
              } else if (error.code === 'ERR_NETWORK') {
                ErrorToast(t('errors:networkConnectionLost'));
              } else {
                ErrorToast(t('errors:attachmentUploadFailed'));
              }
              return;
            }
          }
        }

        let response;

        if (isLibrary) {
          response = isEditing
            ? // serviceType.itineraryserviceid is the ID we need
              await updateLibraryService(
                serviceType.itineraryserviceid,
                payload
              )
            : await createLibraryService(payload);
        } else if (isSaveToLibrary) {
          response = await createLibraryService(payload);
        } else {
          response = isEditing
            ? await updateServiceType(
                tripid,
                tripdetaileddayid,
                serviceType.tripdetaileddayserviceid,
                payload
              )
            : await createServiceType(tripid, tripdetaileddayid, payload);
        }

        if (response.error) {
          ErrorToast(errorText);
        } else {
          SuccessToast(
            t(
              isSaveToLibrary
                ? 'library:libraryServiceSavedSuccessfully'
                : 'trip:serviceTypeSavedSuccessfully'
            )
          );
          if (isSaveToLibrary && onSaveToLibrary) {
            onSaveToLibrary(response.data);
          } else {
            onSave(response.data); // Update parent state with saved data
          }
        }
      }
    } catch (error) {
      // Handle unexpected errors that weren't caught by inner try/catch blocks
      if (error.code === 'ECONNABORTED') {
        ErrorToast(t('errors:requestTimedOut'));
      } else if (error.code === 'ERR_NETWORK') {
        ErrorToast(t('errors:networkConnectionLost'));
      } else {
        ErrorToast(errorText);
      }
    } finally {
      setIsSaving(false);
      setIsSavingToLibrary(false);
    }
  };

  return (
    <Box
      ref={formContainerRef}
      className={`detailed-itinerary-service-types-form ${hasRows ? 'detailed-itinerary-service-types-form--open' : ''}`}
    >
      {!isLoading && !serviceType && !itineraryNavigation && (
        <Box className={'detailed-itinerary-service-types-form-buttons'}>
          <XGDropdownMenu
            isButton={true}
            btnType='primary'
            btnVariant='contained'
            btnSize='large'
            btnText={serviceType ? serviceType?.name : t('add')}
            btnStartIcon={<PlusCircle />}
            options={formatServiceTypeOptions(
              serviceTypes,
              onServiceTypeChange
            )}
            id='detailed-itinerary-service-types-dropdown-menu'
          />
        </Box>
      )}
      {serviceType && (
        <>
          <Box
            id={id}
            data-testid='detailed-itinerary-service-types-skeleton'
            className={`detailed-itinerary-service-types-form-container `}
            {...rest}
          >
            {!itineraryNavigation && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {ServiceTypeIcon && (
                    <div
                      className={`service-type__circle service-type__circle-${iconref}`}
                    >
                      <ServiceTypeIcon size={23.33} />
                    </div>
                  )}
                  <span className='text-xl-semibold'>{serviceTypeName}</span>
                </Box>
                {canDelete && (
                  <div
                    className='detailed-itinerary-service-types-delete-button'
                    onClick={() => {
                      setIsDeletingDayService(true);
                    }}
                  >
                    <Trash size={20} color='#667085' />
                  </div>
                )}
              </Box>
            )}

            {renderFields()}
            {!showCustomOptions ? (
              <Box
                className={
                  'detailed-itinerary-service-types-form-fields-buttons'
                }
                display={'flex'}
                justifyContent={'flex-end'}
              >
                <XGButton
                  data-testid='detailed-itinerary-service-types-form-cancel-button'
                  id='detailed-itinerary-service-types-form-cancel-button'
                  text={t('cancel')}
                  type='secondary'
                  size='medium'
                  variant='outlined'
                  onClick={onCancel}
                  disabled={isSaving || isLoading || isImportingPhotos}
                />
                {!isLibrary && (
                  <Box sx={{ position: 'relative' }}>
                    <XGButton
                      data-testid='detailed-itinerary-service-types-form-save-to-library-button'
                      id='detailed-itinerary-service-types-form-save-to-library-button'
                      text={t('library:saveToLibrary')}
                      type='secondary'
                      size='medium'
                      variant='contained'
                      startIcon={<PlusCircle />}
                      onClick={handleSaveToLibrary}
                      disabled={
                        isSaving ||
                        isLoading ||
                        !serviceType?.tripdetaileddayserviceid ||
                        isSavingToLibrary ||
                        isImportingPhotos
                      }
                    />
                    {isSavingToLibrary && (
                      <CircularProgress
                        id='booking-modal-actions-loader'
                        size={24}
                        sx={{
                          color: blue[500],
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-12px',
                          marginLeft: '-12px'
                        }}
                      />
                    )}
                  </Box>
                )}
                <Box sx={{ position: 'relative' }}>
                  <XGButton
                    data-testid='detailed-itinerary-service-types-form-save-button'
                    id='detailed-itinerary-service-types-form-save-button'
                    text={t('save')}
                    type='primary'
                    size='medium'
                    variant='contained'
                    onClick={handleSave}
                    disabled={
                      isSaving ||
                      isLoading ||
                      isImportingPhotos ||
                      !hasUnsavedChanges
                    }
                  />
                  {isSaving && (
                    <CircularProgress
                      id='booking-modal-actions-loader'
                      size={24}
                      sx={{
                        color: blue[500],
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px'
                      }}
                    />
                  )}
                </Box>
              </Box>
            ) : (
              <Box className='day-service-edit-slideout__footer'>
                <Box
                  className={`day-service-edit-slideout__footer-actions ${isLibrary && !isEditing && 'flex-right--important'}`}
                >
                  {!isLibrary ? (
                    <XGButton
                      text={t('library:saveToLibrary')}
                      type='secondary'
                      size='medium'
                      variant='outlined'
                      onClick={handleSaveToLibrary}
                      startIcon={<Books size={20} />}
                      id='day-service-edit-slideout-save-to-library-button'
                      disabled={
                        isSaving ||
                        isSavingToLibrary ||
                        isLoading ||
                        isImportingPhotos
                      }
                    />
                  ) : (
                    <>
                      {isEditing && (
                        <XGButton
                          text={t('common:delete')}
                          type='error'
                          size='medium'
                          variant='outlined'
                          onClick={() => setIsDeletingDayService(true)}
                          startIcon={<Trash size={20} />}
                          id='library-item-slideout-delete-library-button'
                          disabled={
                            isSaving ||
                            isSavingToLibrary ||
                            isLoading ||
                            isImportingPhotos
                          }
                        />
                      )}
                    </>
                  )}

                  <Box className='day-service-edit-slideout__primary-actions'>
                    <XGButton
                      text={t('common:cancel')}
                      type='secondary'
                      size='medium'
                      variant='outlined'
                      onClick={onCancel}
                      id='day-service-edit-slideout-cancel-button'
                      disabled={
                        isSaving ||
                        isSavingToLibrary ||
                        isLoading ||
                        isImportingPhotos
                      }
                    />

                    <Box className='relative-position'>
                      <XGButton
                        text={t('common:saveChanges')}
                        type='primary'
                        size='medium'
                        variant='contained'
                        onClick={handleSave}
                        disabled={
                          isSaving ||
                          isSavingToLibrary ||
                          isLoading ||
                          isImportingPhotos ||
                          !hasUnsavedChanges
                        }
                        id='day-service-edit-slideout-save-button'
                      />
                      {isSaving && (
                        <CircularProgress
                          id='day-service-edit-slideout-loader-spinner'
                          size={24}
                          className='spinner spinner__primary'
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <DeleteDayService
            data={{
              tripid,
              tripdetaileddayid,
              tripdetaileddayserviceid: serviceType.tripdetaileddayserviceid,
              itineraryserviceid: serviceType.itineraryserviceid
            }}
            id='delete-day-service-component'
            isOpenModal={isDeletingDayService}
            updateIsOpen={(isOpenModal) => {
              setIsDeletingDayService(isOpenModal);
            }}
            onDelete={onDelete}
            isLibrary={isLibrary}
          />
        </>
      )}
    </Box>
  );
};

ServiceTypesForm.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  serviceTypes: PropTypes.array.isRequired,
  onServiceTypeChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  updateFormData: PropTypes.func.isRequired,
  tripid: PropTypes.number,
  tripdetaileddayid: PropTypes.number,
  serviceType: PropTypes.object,
  isLoading: PropTypes.bool,
  canDelete: PropTypes.bool,
  isEditing: PropTypes.bool,
  isLibrary: PropTypes.bool,
  showCustomOptions: PropTypes.bool,
  isLoadingLibraryForm: PropTypes.bool,
  onSaveToLibrary: PropTypes.func,
  dayDate: PropTypes.any,
  toggleKey: PropTypes.string
};

export default ServiceTypesForm;
